import { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, SpinnerGap, MagnifyingGlass } from '@phosphor-icons/react';
import { useAddressSearch } from '../../hooks/useAddressSearch';
import { useAddresses } from '../../hooks/useAddresses';
import { reverseGeocode } from '../../services/mapboxService';
import toast from 'react-hot-toast';

// Lazy: AddressPinConfirm importa mapbox-gl (1.7 MB). Como LocationModal vive
// en UnifiedLayout (raíz), un import estático metería mapbox al main bundle.
// Lazy = chunk separado que solo se descarga cuando el user tap una sugerencia.
const AddressPinConfirm = lazy(() => import('./AddressPinConfirm'));

/**
 * Sheet de selección de dirección. Se abre automáticamente al cargar la app
 * (controlado por LocationContext) y es OBLIGATORIO — no tiene X ni cierra al
 * tocar fuera. Solo cierra cuando el usuario selecciona una dirección válida.
 *
 * Tres caminos para elegir dirección:
 *   1. Pestaña amarilla superior — GPS → AddressPinConfirm
 *   2. Input de búsqueda — autocompletado Mapbox → al tap sugerencia → AddressPinConfirm
 *   3. Lista de direcciones guardadas (selección directa, sin pin confirm)
 *
 * NUEVO flow: las opciones 1 y 2 NO guardan directo. Abren AddressPinConfirm,
 * un fullscreen donde el usuario ajusta el pin al punto exacto y confirma.
 * Solo entonces se guarda. La opción 3 (saved) sigue siendo selección directa
 * (ya están confirmadas previamente).
 *
 * Shape de address guardada:
 *  - `label` = localidad (e.g. "San Luis Tehuiloyocan"), antes era "Casa"/"Oficina"
 *  - `street` = dirección completa
 *  - `coordinates` = lat/lng confirmadas con el pin
 */
export default function LocationModal({ isOpen, onClose }) {
  const { query, setQuery, suggestions, isSearching, selectAddress: selectSearchResult, clear } = useAddressSearch();
  const { addresses, addAddress, selectAddress } = useAddresses();
  const [geoLoading, setGeoLoading] = useState(false);

  // Pin confirm state — cuando está set, se abre AddressPinConfirm encima del modal.
  // Forma: { name, fullAddress, city, coordinates: {lat,lng} }
  const [pendingSuggestion, setPendingSuggestion] = useState(null);

  // Reset search when modal opens
  useEffect(() => {
    if (isOpen) clear();
  }, [isOpen]);

  const notifyLocationUpdated = (coordinates) => {
    if (!coordinates) return;
    window.dispatchEvent(new CustomEvent('locationUpdated', { detail: { coordinates } }));
  };

  // Tap en una sugerencia de Mapbox: NO guarda directo. Hace retrieve para
  // obtener coordenadas y abre AddressPinConfirm con esa data inicial.
  const handleSelectSuggestion = async (suggestion) => {
    const fullAddress = await selectSearchResult(suggestion);
    if (!fullAddress || !fullAddress.coordinates) {
      toast.error('No se pudo obtener la dirección');
      return;
    }
    setPendingSuggestion({
      name: fullAddress.name || suggestion.name,
      fullAddress: fullAddress.fullAddress,
      city: fullAddress.city,
      coordinates: fullAddress.coordinates,
    });
  };

  // GPS: reverse-geocode el punto del usuario y abre AddressPinConfirm para
  // que ajuste el pin. Mismo destino que la suggestion path.
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const result = await reverseGeocode(latitude, longitude);
          if (!result) {
            toast.error('No se pudo obtener tu dirección');
            return;
          }
          setPendingSuggestion({
            name: result.city || result.street || 'Mi ubicación',
            fullAddress: result.fullAddress,
            city: result.city,
            coordinates: result.coordinates,
          });
        } catch {
          toast.error('Error al obtener tu ubicación');
        } finally {
          setGeoLoading(false);
        }
      },
      (error) => {
        setGeoLoading(false);

        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Permiso de ubicación denegado. Ve a la configuración de tu navegador para habilitarlo.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Tu ubicación no está disponible. Verifica que el GPS esté activado.');
            break;
          case error.TIMEOUT:
            toast.error('Se agotó el tiempo de espera. Intenta de nuevo o ingresa tu dirección manualmente.');
            break;
          default:
            toast.error('No pudimos obtener tu ubicación. Intenta ingresarla manualmente.');
            break;
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Saved address tap — selección directa, sin pin confirm (ya están confirmadas).
  const handleSelectSaved = async (addr) => {
    const street = addr.street;
    selectAddress(street);
    localStorage.setItem('userAddress', street);
    // Mantener sincronizada la línea superior del header (city/localidad). Sin
    // esto solo se actualiza `userAddress` (línea inferior) y `userAddressDetail`
    // queda con la localidad anterior. `addr.label` es la localidad guardada.
    localStorage.setItem('userAddressDetail', JSON.stringify({
      street,
      fullAddress: street,
      city: addr.label || '',
      region: '',
    }));
    if (addr.coordinates) {
      const coords = Array.isArray(addr.coordinates)
        ? { lng: addr.coordinates[0], lat: addr.coordinates[1] }
        : addr.coordinates;
      localStorage.setItem('userCoordinates', JSON.stringify(coords));
      notifyLocationUpdated(coords);
    }
    onClose();
  };

  // Confirm del AddressPinConfirm: aquí es donde realmente se guarda la dirección.
  // El label ahora es la localidad (e.g. "San Luis Tehuiloyocan").
  const handlePinConfirm = async ({ locality, street, coordinates }) => {
    await addAddress({
      label: locality,  // ← repurpose: label ahora es la localidad
      street,
      coordinates,
    });
    selectAddress(street);
    localStorage.setItem('userAddress', street);
    localStorage.setItem('userAddressDetail', JSON.stringify({
      street,
      fullAddress: street,
      city: locality,
      region: '',
    }));
    localStorage.setItem('userCoordinates', JSON.stringify(coordinates));
    notifyLocationUpdated(coordinates);
    setPendingSuggestion(null);
    onClose();
    toast.success('Dirección guardada');
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onTouchMove={(e) => e.preventDefault()}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute inset-x-0 bottom-0 max-h-[90dvh] flex flex-col"
              onTouchMove={(e) => e.stopPropagation()}
            >
              {/* Geolocation banner */}
              <button
                onClick={handleGeolocation}
                disabled={geoLoading}
                className="w-full shrink-0 flex items-center justify-between gap-3 px-4 pt-4 pb-10 rounded-t-3xl text-left disabled:opacity-60 active:scale-[0.99] transition-transform"
                style={{ backgroundColor: '#FBBF24' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-900 leading-snug">
                    <span className="font-bold">Compartir tu ubicación actual </span>
                    es la forma más rápida de comprobar tu área
                  </p>
                </div>
                <div className="bg-white rounded-full px-4 py-1.5 flex-shrink-0 flex items-center justify-center min-w-[80px]">
                  {geoLoading ? (
                    <SpinnerGap className="w-4 h-4 text-gray-900 animate-spin" />
                  ) : (
                    <span className="text-xs font-bold text-gray-900">Compartir</span>
                  )}
                </div>
              </button>

              <div className="bg-white rounded-t-3xl flex-1 min-h-0 overflow-hidden touch-none -mt-6 relative flex flex-col">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3" />

                <div className="sticky top-0 bg-white px-5 pt-4 pb-3">
                  <h3 className="text-lg font-bold text-gray-900">¿Dónde te entregamos?</h3>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 touch-auto">
                  <div className="space-y-4">

                    {/* Search input */}
                    <div className="relative">
                      <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar calle, colonia..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-11 pr-10 py-3.5 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gray-50"
                      />
                      {isSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-5 h-5 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Search suggestions */}
                    {suggestions.length > 0 && (
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {suggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 rounded-xl transition-colors"
                          >
                            <MapPin className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm">{suggestion.name}</p>
                              <p className="text-xs text-gray-500 truncate">{suggestion.fullAddress}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Empty state */}
                    {!query && suggestions.length === 0 && addresses.length === 0 && (
                      <p className="text-center text-sm text-gray-400 py-4">
                        Escribe tu dirección o usa tu ubicación actual
                      </p>
                    )}

                    {/* Saved addresses — ahora muestran LOCALIDAD (label) como título.
                        Fallback a 'Mi dirección' si label viene vacío (compat con
                        direcciones guardadas con el formato viejo "Casa"/"Oficina"). */}
                    {addresses.length > 0 && !query && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Direcciones guardadas
                        </p>
                        <div className="space-y-1">
                          {addresses.map((addr) => (
                            <button
                              key={addr._id}
                              onClick={() => handleSelectSaved(addr)}
                              className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 rounded-xl transition-colors"
                            >
                              <MapPin weight="fill" className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm">{addr.label || 'Mi dirección'}</p>
                                <p className="text-xs text-gray-500 truncate">{addr.street}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* AddressPinConfirm — overlay encima del modal cuando hay una suggestion
          o GPS result esperando confirmación con pin. Lazy → chunk con mapbox
          se descarga solo en el momento del primer pendingSuggestion. */}
      {pendingSuggestion && (
        <Suspense fallback={null}>
          <AddressPinConfirm
            isOpen={!!pendingSuggestion}
            onClose={() => setPendingSuggestion(null)}
            initialSuggestion={pendingSuggestion}
            onConfirm={handlePinConfirm}
          />
        </Suspense>
      )}
    </>
  );
}
